'use client';
import React from 'react';
import { ArrowRight, Clock, ShieldCheck, EyeOff, Delete, TrendingUp } from 'lucide-react';
import type { ExamPreset } from '@/data/examPresets';

interface ExamGridProps {
  exams: ExamPreset[];
  onSelect: (exam: ExamPreset) => void;
}

const FEATURES = [
  { icon: ShieldCheck, label: 'Official SSC formula' },
  { icon: EyeOff, label: 'Blind mode' },
  { icon: Delete, label: 'Backspace lock' },
  { icon: TrendingUp, label: 'Progress tracking' },
];

export default function ExamGrid({ exams, onSelect }: ExamGridProps) {
  return (
    <div className="max-w-5xl mx-auto">
      {/* Masthead */}
      <header className="max-w-2xl mb-10">
        <span className="eyebrow">Government exam practice</span>
        <h2 className="text-3xl md:text-4xl text-foreground leading-tight mt-3 mb-4">
          Choose your typing test
        </h2>
        <p className="text-base text-muted-foreground leading-relaxed">
          A TCS iON-style interface running the official SSC net-WPM formula. Pick an exam,
          configure your settings, and practise exactly like the real test.
        </p>
      </header>

      {/* Capability strip */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 mb-10"
        style={{ borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
      >
        {FEATURES.map(({ icon: Icon, label }, i) => (
          <div
            key={label}
            className="flex items-center gap-2.5 py-4 px-4 first:pl-0 text-sm text-muted-foreground"
            style={i > 0 ? { borderLeft: '1px solid var(--border)' } : undefined}
          >
            <Icon size={15} className="text-foreground shrink-0" aria-hidden="true" />
            {label}
          </div>
        ))}
      </div>

      {/* Exam records */}
      <div style={{ borderTop: '1px solid var(--border)' }}>
        {exams.map((exam) => (
          <button
            key={exam.id}
            type="button"
            onClick={() => onSelect(exam)}
            className="w-full text-left group py-6 transition-colors hover:bg-muted"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <div className="flex items-start gap-5 px-2">
              <span
                className="icon-tile text-lg"
                style={{ width: '2.75rem', height: '2.75rem' }}
                aria-hidden="true"
              >
                {exam.icon}
              </span>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h3 className="text-base font-semibold text-foreground leading-snug">
                    {exam.name}
                  </h3>
                  <span className="eyebrow">{exam.shortName}</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1.5 max-w-xl">
                  {exam.blurb}
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-faint">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} /> {exam.durationMin} min
                  </span>
                  <span>{exam.organisation}</span>
                  <span>{exam.language}</span>
                </div>
              </div>

              <div className="flex flex-col items-end gap-3 shrink-0 pl-2">
                <div className="text-right">
                  <div
                    className="font-mono stat-value text-foreground leading-none"
                    style={{ fontSize: '1.25rem' }}
                  >
                    {exam.targetWpm}
                  </div>
                  <div className="eyebrow mt-1.5">target wpm</div>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                  Start
                  <ArrowRight
                    size={14}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
